import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { getStaticRangesList } from './utils';
import PickerComponent from './PickerComponent';
import { formatMonthDateYearWithoutUTC } from '@dataGrid/gridUtilities';
import CustomTextField from '../../../modules/Dispatch/RouteExecution/DriverPopUp/common/CustomTextField';
import DateRange from '@material-ui/icons/DateRange';
import { Popover } from '@material-ui/core';
import appConstants from '../../../../appConstants';

const styles = (theme) => ({
  wrapper: {
    width: '100%'
  },
  fullWidth: {
    width: '100%',
    margin: 0
  },
  errorText: {
    color: theme.ErrorMessageColor,
    fontSize: theme.textFontSize,
    marginLeft: 12
  },
  dateIcon: {
    cursor: 'pointer',
    color: theme.TextColor
  }
});

const defaultDateRange = {
  startDate: new Date(),
  endDate: new Date()
};

const CustomDateRangePicker = ({
  classes,
  value,
  onChange,
  staticRanges,
  minDate,
  maxDate,
  maxRange,
  errorInfo,
  labelText = 'Date Range',
  isFutureDateAllowed = false,
  shouldDisplayDateRange = true,
  includeTodayInStaticRanges = true
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(defaultDateRange);
  const anchorRef = useRef(null);

  // Update internal state when external value changes
  useEffect(() => {
    if (value && value.startDate && value.endDate) {
      setSelectedRange(value);
    } else {
      setSelectedRange(defaultDateRange);
      onChange(defaultDateRange);
    }
  }, [value]);

  const getDisplayValue = () => {
    const showPlaceholderDateRange =
      !shouldDisplayDateRange ||
      !selectedRange ||
      !selectedRange.startDate ||
      !selectedRange.endDate;

    if (showPlaceholderDateRange) {
      return `${appConstants.DATETYPES.MONTHDATEYEAR.format} - ${appConstants.DATETYPES.MONTHDATEYEAR.format}`;
    }
    return `${formatMonthDateYearWithoutUTC({ value: selectedRange.startDate })} - ${formatMonthDateYearWithoutUTC({ value: selectedRange.endDate })}`;
  };

  const handleRangeChange = (selectedRange) => {
    setSelectedRange(selectedRange);
    onChange?.(selectedRange);
  };

  const openPicker = (event) => {
    anchorRef.current = event.currentTarget;
    setIsPickerOpen(true);
  };

  const closePicker = () => {
    setIsPickerOpen(false);
    anchorRef.current = null;
  };

  const staticRangesList = useMemo(
    () => getStaticRangesList(staticRanges, includeTodayInStaticRanges),
    [staticRanges, includeTodayInStaticRanges]
  );

  const getEffectiveMaxDate = useCallback(() => {
    if (maxDate) {
      return maxDate;
    }
    return isFutureDateAllowed ? null : new Date();
  }, [maxDate, isFutureDateAllowed]);

  return (
    <div className={classes.wrapper}>
      <CustomTextField
        label={labelText}
        className={classes.fullWidth}
        classes={{
          inputRoot: classes.inputRoot,
          inputField: classes.inputField
        }}
        id="CustomDateRange"
        value={getDisplayValue()}
        endAdornment={<DateRange className={classes.dateIcon} />}
        onClick={openPicker}
        hideClearIcon
        readOnly
      />
      {isPickerOpen && (
        <Popover
          open={isPickerOpen}
          anchorEl={anchorRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}>
          <PickerComponent
            value={selectedRange}
            onChange={handleRangeChange}
            staticRanges={staticRangesList}
            closePicker={closePicker}
            maxDate={getEffectiveMaxDate()}
            minDate={minDate}
            maxRange={maxRange}
          />
        </Popover>
      )}
      {errorInfo && <span className={classes.errorText}>{errorInfo}</span>}
    </div>
  );
};

export default compose(withStyles(styles), withTheme())(CustomDateRangePicker);
